// SPDX-License-Identifier: GPL-3.0-or-later
//
// Auto-screen a freshly-captured job against the user's preferences. Uses the
// configured 'quick' model, runs synchronously (a few seconds), and is
// best-effort — any failure leaves the application in 'Saved' state so the
// user can still triage it.

import { complete } from './ai/client';
import { parseJson } from './ai/parse';
import { screenSystemPrompt, screenUserPrompt } from './ai/prompts/screen';
import { isScreenResult, type ScreenResult } from './ai/schemas';
import { readJobTxt } from './fs/jobtxt';
import { readProfile } from './fs/profile';
import { getRoot, updateApplicationFields } from './store/applications';
import type { Application } from './types';
import { getWalletState } from './wallet';

export interface ScreenOutcome {
  ran: boolean;
  result?: ScreenResult;
  error?: string;
}

export async function autoScreen(app: Application): Promise<ScreenOutcome> {
  const root = getRoot();
  if (!root) return { ran: false };
  const profile = await readProfile(root);
  const preferences = profile?.preferences?.trim();
  if (!preferences) return { ran: false };
  if (!getWalletState().connection) return { ran: false };

  // Pull description from the freshly-saved job.txt — passing it via the
  // payload would require more plumbing, and the read is cheap.
  let description = '';
  try {
    const txt = await readJobTxt(root, app);
    description = txt?.description ?? '';
  } catch { /* leave empty */ }

  try {
    const res = await complete({
      system: screenSystemPrompt,
      user: screenUserPrompt({
        preferences,
        jobTitle: app.jobTitle,
        companyName: app.companyName,
        jobUrl: app.jobUrl,
        applyType: typeof app.applyType === 'string' ? app.applyType : 'External Link',
        jobDescription: description,
      }),
      maxTokens: 400,
      kind: 'quick',
    });
    const parsed = parseJson(res.content, isScreenResult);
    if (!parsed.ok) {
      return { ran: true, error: parsed.message };
    }
    if (!parsed.value.match) {
      await updateApplicationFields(app.id, {
        status: 'Filtered',
        notes: `Auto-screen: ${parsed.value.reason}`,
      });
    }
    return { ran: true, result: parsed.value };
  } catch (err) {
    return { ran: true, error: err instanceof Error ? err.message : String(err) };
  }
}
